import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import type {
  GuildRankTitle,
  GuildSpecialTitle,
} from "@/core/guild-members/titles"
import type { ActivityPointsMonth } from "@/lib/activity-points-api"
export type MemberId = string
export type ReportingPeriod = string
export type MemberPeriodKey = `${MemberId}:${ReportingPeriod}`
export type PointsInputValue = string
export type MemberPointsDraft = Record<MemberId, PointsInputValue>

export const memberPeriodKey = (
  memberId: MemberId,
  period: ReportingPeriod,
): MemberPeriodKey => `${memberId}:${period}`

export type MembersRosterState = {
  mode: "readonly" | "editing"
  draft: MemberPointsDraft
  pending: boolean
  error: boolean
  invalidMemberId: MemberId | null
}

export type MemberPointsState = {
  mode: "readonly" | "editing"
  draft: PointsInputValue
  pending: boolean
  error: string | null
}

export type MemberTitleDraft = {
  rankTitle: GuildRankTitle | null
  specialTitles: GuildSpecialTitle[]
}

type MemberTitleState = {
  open: boolean
  draft: MemberTitleDraft | null
  error: string | null
}

export type MembersFocusIntent =
  | { area: "roster"; key: ReportingPeriod; target: "edit" | MemberId }
  | { area: "points"; key: MemberPeriodKey; target: "edit" | "input" }
  | { area: "titles"; key: MemberId; target: "error" }

type MembersUiState = {
  rosters: Record<ReportingPeriod, MembersRosterState>
  points: Record<MemberPeriodKey, MemberPointsState>
  titles: Record<MemberId, MemberTitleState>
  focusIntent: MembersFocusIntent | null
}

const readonlyRoster = (): MembersRosterState => ({
  mode: "readonly",
  draft: {},
  pending: false,
  error: false,
  invalidMemberId: null,
})

const readonlyPoints = (): MemberPointsState => ({
  mode: "readonly",
  draft: "",
  pending: false,
  error: null,
})

const closedTitles = (): MemberTitleState => ({
  open: false,
  draft: null,
  error: null,
})
const EMPTY_ROSTER = readonlyRoster()
const EMPTY_POINTS = readonlyPoints()
const CLOSED_TITLES = closedTitles()

export const createMembersUiStore = () => {
  const store = createStore<MembersUiState>(() => ({
    rosters: {},
    points: {},
    titles: {},
    focusIntent: null,
  }))

  const setRoster = (period: ReportingPeriod, state: MembersRosterState) =>
    store.setState((current) => ({
      rosters: { ...current.rosters, [period]: state },
    }))
  const setPoints = (key: MemberPeriodKey, state: MemberPointsState) =>
    store.setState((current) => ({
      points: { ...current.points, [key]: state },
    }))
  const setTitles = (memberId: MemberId, state: MemberTitleState) =>
    store.setState((current) => ({
      titles: { ...current.titles, [memberId]: state },
    }))

  const actions = {
    editRoster: (
      period: ReportingPeriod,
      rows: ActivityPointsMonth["rows"],
    ) => {
      setRoster(period, {
        mode: "editing",
        draft: Object.fromEntries(
          rows.map((row) => [
            row.memberId,
            row.points === null ? "" : String(row.points),
          ]),
        ),
        pending: false,
        error: false,
        invalidMemberId: null,
      })
      const firstMemberId = rows[0]?.memberId
      if (firstMemberId)
        store.setState({
          focusIntent: { area: "roster", key: period, target: firstMemberId },
        })
    },
    updateRosterDraft: (
      period: ReportingPeriod,
      memberId: MemberId,
      value: PointsInputValue,
    ) => {
      const current = store.getState().rosters[period] ?? readonlyRoster()
      setRoster(period, {
        ...current,
        draft: { ...current.draft, [memberId]: value },
        error: false,
        invalidMemberId: null,
      })
    },
    cancelRoster: (period: ReportingPeriod) => {
      setRoster(period, readonlyRoster())
      store.setState({
        focusIntent: { area: "roster", key: period, target: "edit" },
      })
    },
    rejectRoster: (
      period: ReportingPeriod,
      fallback: MembersRosterState,
      memberId: MemberId,
    ) => {
      const current = store.getState().rosters[period] ?? fallback
      setRoster(period, { ...current, error: false, invalidMemberId: memberId })
      store.setState({
        focusIntent: { area: "roster", key: period, target: memberId },
      })
    },
    beginRosterSave: (
      period: ReportingPeriod,
      fallback: MembersRosterState,
    ) => {
      const current = store.getState().rosters[period] ?? fallback
      setRoster(period, {
        ...current,
        pending: true,
        error: false,
        invalidMemberId: null,
      })
    },
    completeRosterSave: (period: ReportingPeriod) => {
      setRoster(period, readonlyRoster())
      store.setState({
        focusIntent: { area: "roster", key: period, target: "edit" },
      })
    },
    failRosterSave: (period: ReportingPeriod, fallback: MembersRosterState) => {
      const current = store.getState().rosters[period] ?? fallback
      setRoster(period, {
        ...current,
        mode: "editing",
        pending: false,
        error: true,
      })
    },
    editPoints: (key: MemberPeriodKey, points: number | null) => {
      setPoints(key, {
        mode: "editing",
        draft: points === null ? "" : String(points),
        pending: false,
        error: null,
      })
      store.setState({ focusIntent: { area: "points", key, target: "input" } })
    },
    updatePointsDraft: (key: MemberPeriodKey, value: PointsInputValue) => {
      const current = store.getState().points[key] ?? readonlyPoints()
      setPoints(key, { ...current, draft: value, error: null })
    },
    cancelPoints: (key: MemberPeriodKey) => {
      setPoints(key, readonlyPoints())
      store.setState({ focusIntent: { area: "points", key, target: "edit" } })
    },
    rejectPoints: (
      key: MemberPeriodKey,
      fallback: MemberPointsState,
      error: string,
    ) => {
      const current = store.getState().points[key] ?? fallback
      setPoints(key, { ...current, error })
      store.setState({ focusIntent: { area: "points", key, target: "input" } })
    },
    beginPointsSave: (key: MemberPeriodKey, fallback: MemberPointsState) => {
      const current = store.getState().points[key] ?? fallback
      setPoints(key, { ...current, pending: true, error: null })
    },
    completePointsSave: (key: MemberPeriodKey) => {
      setPoints(key, readonlyPoints())
      store.setState({ focusIntent: { area: "points", key, target: "edit" } })
    },
    failPointsSave: (key: MemberPeriodKey, fallback: MemberPointsState) => {
      const current = store.getState().points[key] ?? fallback
      setPoints(key, {
        ...current,
        mode: "editing",
        pending: false,
        error: "Activity points were not saved.",
      })
    },
    openTitles: (memberId: MemberId, draft: MemberTitleDraft) =>
      setTitles(memberId, { open: true, draft, error: null }),
    closeTitles: (memberId: MemberId) => setTitles(memberId, closedTitles()),
    updateTitleDraft: (memberId: MemberId, draft: MemberTitleDraft) => {
      const current = store.getState().titles[memberId] ?? closedTitles()
      setTitles(memberId, { ...current, draft })
    },
    beginTitleSave: (memberId: MemberId) => {
      const current = store.getState().titles[memberId] ?? closedTitles()
      setTitles(memberId, { ...current, error: null })
    },
    completeTitleSave: (memberId: MemberId) =>
      setTitles(memberId, closedTitles()),
    failTitleSave: (memberId: MemberId, error: string) => {
      const current = store.getState().titles[memberId] ?? closedTitles()
      setTitles(memberId, { ...current, error })
      store.setState({
        focusIntent: { area: "titles", key: memberId, target: "error" },
      })
    },
    consumeFocusIntent: (intent: MembersFocusIntent) => {
      if (store.getState().focusIntent === intent)
        store.setState({ focusIntent: null })
    },
  }

  return { store, actions }
}

export type MembersUiStore = ReturnType<typeof createMembersUiStore>

export const useMembersRosterState = (
  membersStore: MembersUiStore,
  period: ReportingPeriod,
) =>
  useStore(membersStore.store, (state) => state.rosters[period] ?? EMPTY_ROSTER)

export const useMemberPointsState = (
  membersStore: MembersUiStore,
  key: MemberPeriodKey,
) => useStore(membersStore.store, (state) => state.points[key] ?? EMPTY_POINTS)

export const useMemberTitleState = (
  membersStore: MembersUiStore,
  memberId: MemberId,
) =>
  useStore(
    membersStore.store,
    (state) => state.titles[memberId] ?? CLOSED_TITLES,
  )

export const useMembersFocusIntent = (membersStore: MembersUiStore) =>
  useStore(membersStore.store, (state) => state.focusIntent)

const membersRouteApi = getRouteApi("/_authed/_admin/members")

export const useMembersRouteStore = () =>
  membersRouteApi.useRouteContext({ select: (context) => context.membersStore })
